/**
 * Organization Sponsors - Sponsor marketplace for organizations
 * Organizations can bring in local advertisers and act as sales channels
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Drawer } from '@/components/campus';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Building2,
  Plus,
  Search,
  ExternalLink,
  Mail,
  Phone,
  DollarSign,
  Calendar,
  Edit2,
  Trash2,
  TrendingUp,
  Users,
  MoreVertical,
  CheckCircle,
  Pause,
  AlertCircle,
  Target,
  ChevronRight,
  Loader2,
  X,
  Megaphone,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { 
  PerformanceSummary, 
  CampusMetrics, 
  OrganizationMetrics,
  EventMetrics,
  useSponsorAnalytics 
} from '@/components/sponsors/SponsorAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sponsor type labels
const SPONSOR_TYPES = {
  local_business: { label: 'Local Business', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  event_sponsor: { label: 'Event Sponsor', color: 'bg-violet-500/20 text-violet-400 border-violet-500/30' },
  brand_partner: { label: 'Brand Partner', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  discovery_promo: { label: 'Discovery Promo', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
};

// Status config
const STATUS_CONFIG = {
  prospect: { label: 'Prospect', icon: Target, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  active: { label: 'Active', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  paused: { label: 'Paused', icon: Pause, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  churned: { label: 'Churned', icon: AlertCircle, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
};

// Glass panel
const GlassPanel = ({ children, className = '', ...props }) => (
  <div className={cn('bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl', className)} {...props}>
    {children}
  </div>
);

// Stat card
const StatCard = ({ label, value, icon: Icon, color = 'text-cyan-400', prefix = '' }) => (
  <GlassPanel className="p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-white/50 text-sm">{label}</p>
        <p className={cn('text-2xl font-mono font-bold mt-1', color)}>
          {prefix}{typeof value === 'number' ? value.toLocaleString() : value}
        </p>
      </div>
      <Icon className={cn('w-8 h-8', color)} />
    </div>
  </GlassPanel>
);

// Sponsor card
const SponsorCard = ({ sponsor, onEdit, onDelete, onStatusChange, onViewDetails }) => {
  // Get sponsor_type from metadata if not directly on sponsor
  const sponsorType = sponsor.metadata?.sponsor_type || sponsor.sponsor_type || 'local_business';
  const status = sponsor.metadata?.status || 'prospect';
  const typeConfig = SPONSOR_TYPES[sponsorType] || SPONSOR_TYPES.local_business;
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.prospect;
  const StatusIcon = statusConfig.icon;

  return (
    <GlassPanel 
      className="p-5 hover:border-white/[0.12] transition-all cursor-pointer group"
      onClick={() => onViewDetails(sponsor)}
      data-testid={`sponsor-card-${sponsor.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Logo */}
        <div className="w-14 h-14 rounded-xl bg-white/[0.05] flex items-center justify-center flex-shrink-0 overflow-hidden">
          {sponsor.logo_url ? (
            <img src={sponsor.logo_url} alt={sponsor.name} className="w-full h-full object-contain p-2" />
          ) : (
            <Building2 className="w-7 h-7 text-white/30" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-medium text-white group-hover:text-cyan-400 transition-colors">
                {sponsor.name}
              </h3>
              <div className="flex items-center gap-2 mt-1.5">
                <Badge className={cn('text-xs', typeConfig.color, 'border')}>
                  {typeConfig.label}
                </Badge>
                <div className={cn('flex items-center gap-1 px-2 py-0.5 rounded-full text-xs', statusConfig.bg, statusConfig.color, statusConfig.border, 'border')}>
                  <StatusIcon className="w-3 h-3" />
                  {statusConfig.label}
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEdit(sponsor)}>
                <Edit2 className="w-4 h-4 text-white/50 hover:text-white" />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onDelete(sponsor)}>
                <Trash2 className="w-4 h-4 text-white/50 hover:text-rose-400" />
              </Button>
            </div>
          </div>

          {sponsor.description && (
            <p className="text-sm text-white/50 mt-2 line-clamp-2">{sponsor.description}</p>
          )}

          {/* Contact Info */}
          <div className="flex items-center gap-4 mt-3 text-xs text-white/40">
            {sponsor.email && (
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" /> {sponsor.email}
              </span>
            )}
            {sponsor.website && (
              <span className="flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Website
              </span>
            )}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/40 transition-colors flex-shrink-0" />
      </div>
    </GlassPanel>
  );
};

// Sponsor Form
const SponsorForm = ({ sponsor, organizationId, onSave, onClose }) => {
  // Map metadata fields to form state
  const metadata = sponsor?.metadata || {};
  const [form, setForm] = useState({
    name: sponsor?.name || '',
    organization_id: organizationId,
    sponsor_type: metadata.sponsor_type || sponsor?.sponsor_type || 'local_business',
    logo_url: sponsor?.logo_url || '',
    website: sponsor?.website || '',  // Database column name
    description: sponsor?.description || '',
    contact_name: sponsor?.contact_name || '',
    contact_title: sponsor?.contact_title || '',  // Database has contact_title
    email: sponsor?.email || '',  // Database column name
    phone: sponsor?.phone || '',  // Database column name
    notes: sponsor?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Sponsor name is required');
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Sponsor Name *</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            placeholder="e.g., Local Pizza Shop"
            data-testid="sponsor-name-input"
          />
        </div>

        <div>
          <Label htmlFor="sponsor_type">Sponsor Type</Label>
          <Select value={form.sponsor_type} onValueChange={(v) => setForm({ ...form, sponsor_type: v })}>
            <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/[0.1]" data-testid="sponsor-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="local_business">Local Business Ad</SelectItem>
              <SelectItem value="event_sponsor">Event Sponsor</SelectItem>
              <SelectItem value="brand_partner">Brand Partner</SelectItem>
              <SelectItem value="discovery_promo">Discovery Feed Promo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            rows={3}
            placeholder="Brief description of the sponsor..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="logo_url">Logo URL</Label>
            <Input
              id="logo_url"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              placeholder="https://..."
            />
          </div>
          <div>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="pt-4 border-t border-white/[0.06]">
        <h4 className="text-sm font-medium text-white mb-3">Contact Information</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contact_name">Contact Name</Label>
            <Input
              id="contact_name"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            />
          </div>
          <div>
            <Label htmlFor="contact_title">Title</Label>
            <Input
              id="contact_title"
              value={form.contact_title}
              onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              placeholder="e.g., Marketing Manager"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            />
          </div>
        </div>
      </div>

      {/* Internal Notes */}
      <div className="pt-4 border-t border-white/[0.06]">
        <Label htmlFor="notes">Internal Notes</Label>
        <Textarea
          id="notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          rows={2}
          placeholder="Internal notes about this sponsor..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.06]">
        <Button type="button" variant="outline" onClick={onClose} className="border-white/10">
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={saving}
          className="bg-cyan-500 hover:bg-cyan-600"
          data-testid="save-sponsor-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {sponsor ? 'Update Sponsor' : 'Add Sponsor'}
        </Button>
      </div>
    </form>
  );
};

// Sponsor Detail Drawer
const SponsorDetailDrawer = ({ sponsor, isOpen, onClose, onEdit, onStatusChange, session }) => {
  const [activeTab, setActiveTab] = useState('details');
  
  // Fetch analytics when sponsor is selected
  const { data: analyticsData, loading: analyticsLoading } = useSponsorAnalytics(
    sponsor?.id, 
    session,
    'last_30_days'
  );
  
  if (!sponsor) return null;
  
  // Get sponsor_type and status from metadata
  const sponsorType = sponsor.metadata?.sponsor_type || sponsor.sponsor_type || 'local_business';
  const status = sponsor.metadata?.status || sponsor.status || 'prospect';
  const typeConfig = SPONSOR_TYPES[sponsorType] || SPONSOR_TYPES.local_business;
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.prospect;
  const StatusIcon = statusConfig.icon;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Sponsor Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-2xl bg-white/[0.05] flex items-center justify-center flex-shrink-0 overflow-hidden">
            {sponsor.logo_url ? (
              <img src={sponsor.logo_url} alt={sponsor.name} className="w-full h-full object-contain p-2" />
            ) : (
              <Building2 className="w-10 h-10 text-white/30" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white">{sponsor.name}</h2>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={cn('text-xs', typeConfig.color, 'border')}>
                {typeConfig.label}
              </Badge>
              <div className={cn('flex items-center gap-1 px-2 py-0.5 rounded-full text-xs', statusConfig.bg, statusConfig.color, statusConfig.border, 'border')}>
                <StatusIcon className="w-3 h-3" />
                {statusConfig.label}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => onEdit(sponsor)} className="border-white/10">
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/[0.06] pb-1">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
              activeTab === 'details' 
                ? 'text-white bg-white/[0.05] border-b-2 border-cyan-400' 
                : 'text-white/50 hover:text-white'
            )}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2',
              activeTab === 'analytics' 
                ? 'text-white bg-white/[0.05] border-b-2 border-cyan-400' 
                : 'text-white/50 hover:text-white'
            )}
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
            {/* Description */}
            {sponsor.description && (
              <div className="p-4 bg-white/[0.02] rounded-xl">
                <p className="text-white/70">{sponsor.description}</p>
              </div>
            )}

            {/* Revenue Stats */}
            <div className="grid grid-cols-1 gap-4">
              <GlassPanel className="p-4">
                <p className="text-xs text-white/40 uppercase tracking-wider">Revenue Generated</p>
                <p className="text-2xl font-mono font-bold text-cyan-400 mt-1">
                  ${(sponsor.total_revenue_generated || 0).toLocaleString()}
                </p>
              </GlassPanel>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-white/60 uppercase tracking-wider">Contact</h3>
              <div className="space-y-2">
                {sponsor.contact_name && (
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <Users className="w-4 h-4 text-white/40" />
                    {sponsor.contact_name} {sponsor.contact_title && `(${sponsor.contact_title})`}
                  </div>
                )}
                {sponsor.email && (
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <Mail className="w-4 h-4 text-white/40" />
                    <a href={`mailto:${sponsor.email}`} className="hover:text-cyan-400">{sponsor.email}</a>
                  </div>
                )}
                {sponsor.phone && (
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <Phone className="w-4 h-4 text-white/40" />
                    {sponsor.phone}
                  </div>
                )}
                {sponsor.website && (
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <ExternalLink className="w-4 h-4 text-white/40" />
                    <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400">{sponsor.website}</a>
                  </div>
                )}
              </div>
            </div>

            {/* Campaigns */}
            {sponsor.campaigns?.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-white/60 uppercase tracking-wider">Associated Campaigns</h3>
                <div className="space-y-2">
                  {sponsor.campaigns.map((campaign) => (
                    <div key={campaign.id} className="p-3 bg-white/[0.02] rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white font-medium">{campaign.name}</p>
                        <p className="text-xs text-white/40">{campaign.status}</p>
                      </div>
                      <Badge className="text-xs">{campaign.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {sponsor.notes && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-white/60 uppercase tracking-wider">Internal Notes</h3>
                <p className="text-sm text-white/50 p-3 bg-white/[0.02] rounded-lg">{sponsor.notes}</p>
              </div>
            )}

            {/* Status Actions */}
            <div className="pt-4 border-t border-white/[0.06]">
              <h3 className="text-sm font-medium text-white/60 uppercase tracking-wider mb-3">Update Status</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  const isActive = status === key;
                  return (
                    <Button
                      key={key}
                      variant="outline"
                      size="sm"
                      className={cn(
                        'border',
                        isActive ? cn(config.bg, config.border, config.color) : 'border-white/10 text-white/60'
                      )}
                      onClick={() => onStatusChange(sponsor.id, key)}
                      disabled={isActive}
                    >
                      <Icon className="w-3 h-3 mr-1.5" />
                      {config.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          /* Analytics Tab */
          <div className="space-y-6" data-testid="sponsor-analytics-tab">
            {/* Performance Summary */}
            <PerformanceSummary 
              data={analyticsData?.summary} 
              loading={analyticsLoading} 
            />
            
            {/* Breakdown Metrics */}
            <div className="grid grid-cols-1 gap-4">
              <EventMetrics 
                data={{ events: analyticsData?.top_events, total_events: analyticsData?.top_events?.length || 0 }} 
                loading={analyticsLoading} 
              />
              <CampusMetrics 
                data={{ campuses: analyticsData?.top_campuses, total_campuses: analyticsData?.top_campuses?.length || 0 }} 
                loading={analyticsLoading} 
              />
              <OrganizationMetrics 
                data={{ organizations: analyticsData?.top_organizations, total_organizations: analyticsData?.top_organizations?.length || 0 }} 
                loading={analyticsLoading} 
              />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

// Main Component
export const OrgSponsors = () => {
  const { session, organization, organizations } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Get effective organization ID - use organization.id or first org from organizations array
  const effectiveOrgId = organization?.id || (organizations?.[0]?.organization_id);

  // Fetch sponsors
  const fetchSponsors = useCallback(async () => {
    if (!session?.access_token || !effectiveOrgId) {
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams({ organization_id: effectiveOrgId });
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const res = await fetch(`${API_URL}/api/org-sponsors?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setSponsors(data.sponsors || []);
        setStats(data.stats || {});
      }
    } catch (err) {
      console.error('Failed to fetch sponsors:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, effectiveOrgId, statusFilter]);

  useEffect(() => {
    fetchSponsors();
  }, [fetchSponsors]);

  // Create/Update sponsor
  const handleSaveSponsor = async (formData) => {
    try {
      const url = editingSponsor
        ? `${API_URL}/api/org-sponsors/${editingSponsor.id}`
        : `${API_URL}/api/org-sponsors`;

      const res = await fetch(url, {
        method: editingSponsor ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success(editingSponsor ? 'Sponsor updated' : 'Sponsor added');
        fetchSponsors();
        setIsFormOpen(false);
        setEditingSponsor(null);
      } else {
        const error = await res.json();
        // Handle Pydantic v2 validation errors (array format) and string format
        let errorMessage = 'Failed to save sponsor';
        if (error.detail) {
          if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else if (Array.isArray(error.detail)) {
            // Pydantic v2 format: [{type, loc, msg, input, url}]
            errorMessage = error.detail.map(e => e.msg).join(', ');
          }
        }
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Failed to save sponsor:', err);
      toast.error('Failed to save sponsor');
    }
  };

  // Delete sponsor
  const handleDeleteSponsor = async (sponsor) => {
    if (!confirm(`Deactivate sponsor "${sponsor.name}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/org-sponsors/${sponsor.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Sponsor deactivated');
        fetchSponsors();
        if (selectedSponsor?.id === sponsor.id) {
          setIsDetailOpen(false);
          setSelectedSponsor(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete sponsor:', err);
    }
  };

  // Update status
  const handleStatusChange = async (sponsorId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/org-sponsors/${sponsorId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success(`Status updated to ${newStatus}`);
        fetchSponsors();
        // Update selected sponsor if detail is open
        if (selectedSponsor?.id === sponsorId) {
          const updated = await res.json();
          setSelectedSponsor(updated.sponsor);
        }
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  // View sponsor details
  const handleViewDetails = async (sponsor) => {
    try {
      const res = await fetch(`${API_URL}/api/org-sponsors/${sponsor.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSponsor(data);
        setIsDetailOpen(true);
      }
    } catch (err) {
      setSelectedSponsor(sponsor);
      setIsDetailOpen(true);
    }
  };

  // Filter sponsors
  const filteredSponsors = sponsors.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          <p className="text-white/50 text-sm">Loading sponsors...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="org-sponsors-page" className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-white flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-violet-400" />
            Sponsors
          </h1>
          <p className="text-white/50 mt-1">Manage your local advertisers and brand partners</p>
        </div>

        <Button 
          onClick={() => { setEditingSponsor(null); setIsFormOpen(true); }}
          className="bg-violet-500 hover:bg-violet-600"
          data-testid="add-sponsor-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Sponsor
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Sponsors" value={stats.total_sponsors || 0} icon={Building2} color="text-violet-400" />
        <StatCard label="Active" value={stats.active_sponsors || 0} icon={CheckCircle} color="text-emerald-400" />
        <StatCard label="Prospects" value={stats.prospects || 0} icon={Target} color="text-blue-400" />
        <StatCard label="Deal Value" value={stats.total_deal_value || 0} icon={DollarSign} color="text-amber-400" prefix="$" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            placeholder="Search sponsors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white/[0.03] border-white/[0.1]"
            data-testid="search-sponsors-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white/[0.03] border-white/[0.1]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="prospect">Prospects</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="churned">Churned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sponsors List */}
      {filteredSponsors.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredSponsors.map((sponsor) => (
            <SponsorCard
              key={sponsor.id}
              sponsor={sponsor}
              onEdit={(s) => { setEditingSponsor(s); setIsFormOpen(true); }}
              onDelete={handleDeleteSponsor}
              onStatusChange={handleStatusChange}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      ) : (
        <GlassPanel className="p-12 text-center">
          <Megaphone className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Sponsors Yet</h3>
          <p className="text-white/50 mb-4 max-w-md mx-auto">
            Start building your sponsor network! Add local businesses, event sponsors, or brand partners.
          </p>
          <Button onClick={() => setIsFormOpen(true)} className="bg-violet-500 hover:bg-violet-600">
            <Plus className="w-4 h-4 mr-2" /> Add First Sponsor
          </Button>
        </GlassPanel>
      )}

      {/* Form Drawer */}
      <Drawer
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingSponsor(null); }}
        title={editingSponsor ? 'Edit Sponsor' : 'Add New Sponsor'}
        width="lg"
      >
        <SponsorForm
          sponsor={editingSponsor}
          organizationId={effectiveOrgId}
          onSave={handleSaveSponsor}
          onClose={() => { setIsFormOpen(false); setEditingSponsor(null); }}
        />
      </Drawer>

      {/* Detail Drawer */}
      <SponsorDetailDrawer
        sponsor={selectedSponsor}
        isOpen={isDetailOpen}
        onClose={() => { setIsDetailOpen(false); setSelectedSponsor(null); }}
        onEdit={(s) => { setIsDetailOpen(false); setEditingSponsor(s); setIsFormOpen(true); }}
        onStatusChange={handleStatusChange}
        session={session}
      />
    </div>
  );
};

export default OrgSponsors;
